import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';

interface CostSavings {
  actualCostPerDose: number;
  standardCostPerDose: number;
  actualCostPerWeek: number;
  standardCostPerWeek: number;
  monthlySpend: number;
  estimatedMonthlySavings: number;
  savingsThisMonth: number;
  averageMonthlySavings: number;
  lifetimeSavings: number;
  goldenDoseSavings: number;
  currency: string;
  pricingAccuracy?: {
    usedDefaultPricing: boolean;
    countriesWithGaps: string[];
    accuracyPercentage: number;
  };
}





const CostAnalysisPage: React.FC = () => {
  const { user } = useAuth();
  const [costSavings, setCostSavings] = useState<CostSavings | null>(null);
  const [loadingCostSavings, setLoadingCostSavings] = useState(false);
  const [userCountry, setUserCountry] = useState('Australia');

  // Utility for currency formatting
  const formatCurrency = (amount: number, currency: string) => {
    // Map currency code to symbol only
    const symbolMap: Record<string, string> = {
      'AUD': '$',
      'CAD': '$',
      'USD': '$',
      'GBP': '£',
      'EUR': '€',
    };
    const symbol = symbolMap[currency] || currency;
    // Always show symbol only, no prefix
    return `${symbol}${amount.toFixed(2)}`;
  };

  const fetchCostSavings = async () => {
    setLoadingCostSavings(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No authentication token found');
        return;
      }

      const response = await fetch('/api/cost-savings', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to fetch cost savings');
      
      const data = await response.json();
      setCostSavings(data);
    } catch (error) {
      console.error('Error fetching cost savings:', error);
    } finally {
      setLoadingCostSavings(false);
    }
  };

  useEffect(() => {
    if (user?.country) {
      setUserCountry(user.country);
    }
  }, [user?.country]);

  useEffect(() => {
    fetchCostSavings();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">Cost Savings Analysis</h1>
          <p className="text-gray-600 dark:text-gray-400">Track your savings and optimize your medication costs</p>
        </div>

        {/* Cost Savings Overview */}
        <div className="mb-8 sm:mb-12">
          <div className="flex items-center mb-4 sm:mb-6">
            <div className="flex-shrink-0 w-6 h-6 sm:w-8 sm:h-8 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold text-xs sm:text-sm mr-2 sm:mr-3">
              1
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">Cost Savings Analysis</h2>
            {costSavings && <span className="ml-2 text-xs sm:text-sm text-gray-500 dark:text-gray-400">({costSavings.currency})</span>}
          </div>
          
          <div className="bg-orange-50 dark:bg-orange-900 rounded-lg p-4 sm:p-6 border border-orange-200 dark:border-orange-700 mb-6">
            <div className="mb-4">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">How We Calculate Your Savings</h3>
              <div className="text-xs sm:text-sm text-gray-700 dark:text-gray-300 space-y-2">
                <p><strong>Standard Approach:</strong> We assume you would use exactly 4 doses per pen (as labeled) and discard any remaining medication.</p>
                <p><strong>Your Approach:</strong> We calculate your actual cost per dose based on your real usage patterns, including any Golden Doses extracted.</p>
                <p><strong>Savings Calculation:</strong> The difference between what you would have spent using the standard approach versus your actual spending.</p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-6">
              <div className="bg-white dark:bg-gray-700 rounded-lg p-3 sm:p-4 border border-green-200 dark:border-gray-600">
                <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-1 text-sm sm:text-base">Lifetime Savings</h4>
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">Total savings since you started tracking</p>
                {costSavings ? (
                  <p className={`text-xl sm:text-2xl font-bold ${costSavings.lifetimeSavings >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(costSavings.lifetimeSavings, costSavings.currency)}
                  </p>
                ) : (
                  <p className="text-gray-400">Loading...</p>
                )}
              </div>
              <div className="bg-white dark:bg-gray-700 rounded-lg p-3 sm:p-4 border border-green-200 dark:border-gray-600">
                <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-1 text-sm sm:text-base">This Month's Savings</h4>
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">Savings in the current month</p>
                {costSavings ? (
                  <p className={`text-xl sm:text-2xl font-bold ${costSavings.savingsThisMonth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(costSavings.savingsThisMonth, costSavings.currency)}
                  </p>
                ) : (
                  <p className="text-gray-400">Loading...</p>
                )}
              </div>
              <div className="bg-white dark:bg-gray-700 rounded-lg p-3 sm:p-4 border border-green-200 dark:border-gray-600">
                <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-1 text-sm sm:text-base">Monthly Average</h4>
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">Average monthly savings over time</p>
                {costSavings ? (
                  <p className={`text-xl sm:text-2xl font-bold ${costSavings.averageMonthlySavings >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(costSavings.averageMonthlySavings, costSavings.currency)}
                  </p>
                ) : (
                  <p className="text-gray-400">Loading...</p>
                )}
              </div>
            </div>

            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              💡 <strong>Tip:</strong> Change your currency using the 'Settings' link in the navigation bar for more accurate calculations.
            </p>
          </div>
          
          {/* Pricing Accuracy Alert */}
          {costSavings?.pricingAccuracy?.usedDefaultPricing && (
            <div className="mb-4 p-4 bg-yellow-50 dark:bg-yellow-900 border border-yellow-200 dark:border-yellow-700 rounded-lg">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h4 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                    Pricing Estimate Notice
                  </h4>
                  <div className="mt-1 text-sm text-yellow-700 dark:text-yellow-300">
                    <p>
                      Some cost calculations are using estimated pricing for your country ({userCountry}). 
                      For more accurate cost analysis, please add specific pen costs or contact support to add pricing for your country.
                    </p>
                    {costSavings.pricingAccuracy.countriesWithGaps.length > 0 && (
                      <p className="mt-1">
                        <strong>Countries needing pricing data:</strong> {costSavings.pricingAccuracy.countriesWithGaps.join(', ')}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {loadingCostSavings ? (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
              <p className="text-gray-600 dark:text-gray-400 mt-2">Loading cost data...</p>
            </div>
          ) : costSavings ? (
            <div className="space-y-6">
              {/* Cost Comparison */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Per Dose Costs */}
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-3">Cost per Dose</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-300">Standard approach:</span>
                      <span className="font-semibold text-gray-800 dark:text-gray-100">{formatCurrency(costSavings.standardCostPerDose, costSavings.currency)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-300">Your approach:</span>
                      <span className="font-semibold text-purple-600 dark:text-purple-400">{formatCurrency(costSavings.actualCostPerDose, costSavings.currency)}</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t">
                      <span className="text-gray-600 dark:text-gray-300">Savings per dose:</span>
                      <span className={`font-semibold ${(costSavings.standardCostPerDose - costSavings.actualCostPerDose) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(costSavings.standardCostPerDose - costSavings.actualCostPerDose, costSavings.currency)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Per Month Costs */}
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-3">Cost per Month</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-300">Standard approach:</span>
                      <span className="font-semibold text-gray-800 dark:text-gray-100">{formatCurrency(costSavings.standardCostPerWeek * 4.33, costSavings.currency)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-300">Your approach:</span>
                      <span className="font-semibold text-purple-600 dark:text-purple-400">{formatCurrency(costSavings.actualCostPerWeek * 4.33, costSavings.currency)}</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t">
                      <span className="text-gray-600 dark:text-gray-300">Savings per month:</span>
                      <span className={`font-semibold ${(costSavings.standardCostPerWeek - costSavings.actualCostPerWeek) * 4.33 >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency((costSavings.standardCostPerWeek - costSavings.actualCostPerWeek) * 4.33, costSavings.currency)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Golden Dose Analytics */}
              {costSavings.goldenDoseSavings > 0 && (
                <div className="bg-yellow-50 dark:bg-yellow-900 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-yellow-800 dark:text-yellow-200 mb-3 flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M10 15l-5.878 3.09 1.122-6.545L.488 6.91l6.561-.955L10 0l2.951 5.955 6.561.955-4.756 4.635 1.122 6.545z"/>
                    </svg>
                    Golden Dose Savings
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="text-center">
                      <p className="text-sm text-yellow-700 dark:text-yellow-300 mb-1">Total Golden Dose Savings</p>
                      <p className="text-xl font-bold text-yellow-800 dark:text-yellow-200">{formatCurrency(costSavings.goldenDoseSavings, costSavings.currency)}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-yellow-700 dark:text-yellow-300 mb-1">% of Total Savings</p>
                      <p className="text-xl font-bold text-yellow-800 dark:text-yellow-200">
                        {costSavings.lifetimeSavings > 0 
                          ? `${((costSavings.goldenDoseSavings / costSavings.lifetimeSavings) * 100).toFixed(1)}%`
                          : '0%'}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <p>No cost data available. Add pen costs to see your savings analysis.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CostAnalysisPage; 